/**
 * Supabase Client Configuration - Simplified Version
 * 
 * React + Vite 환경에서 간소화된 Supabase 클라이언트
 * - 기본적인 클라이언트 생성
 * - Cookie Storage 사용
 * - ERR_CONNECTION_CLOSED 시 페이지 리로드 전략
 */

import { createBrowserClient, SupabaseClient, parseCookieHeader, serializeCookieHeader } from '@supabase/ssr';
import { getSupabaseConfig, isDevelopment } from './env';
import type { Database } from '@/integrations/supabase/types';

// 환경 변수 검증
const validateEnvironment = () => {
  const config = getSupabaseConfig();
  if (!config.url || !config.anonKey) {
    throw new Error('Missing Supabase environment variables. Please check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
  }
  return config;
};

/**
 * 브라우저 쿠키 처리 유틸리티
 */
const createCookieHandler = () => {
  return {
    getAll() {
      if (typeof document === 'undefined') return [];
      return parseCookieHeader(document.cookie);
    },
    
    setAll(cookiesToSet: Array<{ 
      name: string; 
      value: string; 
      options?: {
        httpOnly?: boolean;
        secure?: boolean;
        sameSite?: 'strict' | 'lax' | 'none';
        maxAge?: number;
        path?: string;
        domain?: string;
      };
    }>) {
      if (typeof document === 'undefined') return;
      
      cookiesToSet.forEach(({ name, value, options }) => {
        document.cookie = serializeCookieHeader(name, value, {
          ...options,
          path: '/',
          httpOnly: false,
          secure: window.location.protocol === 'https:',
          sameSite: 'lax',
          maxAge: 60 * 60 * 24 * 30, // 30일
        });
      });
    },
  };
};

/**
 * Simple Supabase client creation
 */
function createSupabaseClient(): SupabaseClient<Database> {
  const config = validateEnvironment();
  
  const client = createBrowserClient<Database>(config.url, config.anonKey, {
    cookies: createCookieHandler(),
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      debug: false, // 디버그 로그 비활성화
      storageKey: 'wholsee-supabase-auth',
    },
    realtime: {
      params: {
        eventsPerSecond: 0,
      },
    },
    global: {
      headers: {
        'x-client-info': 'wholsee-trek@1.0.0',
        'x-client-type': 'simple',
      },
    },
    db: {
      schema: 'public',
    },
  });

  // Auth state monitoring은 AuthProvider에서만 처리
  // 중복 리스너 등록 방지를 위해 여기서는 제거

  return client;
}

/**
 * Singleton Supabase client instance
 */
let supabaseInstance: SupabaseClient<Database> | null = null;

/**
 * Get Supabase client instance
 */
export function getSupabaseClient(): SupabaseClient<Database> {
  if (supabaseInstance) {
    return supabaseInstance;
  }
  
  console.log('🔧 Creating Supabase client instance');
  supabaseInstance = createSupabaseClient();
  return supabaseInstance;
}

/**
 * Simple auth utilities
 */
export const auth = {
  async signIn(email: string, password: string) {
    const client = getSupabaseClient();
    const { data, error } = await client.auth.signInWithPassword({ 
      email, 
      password 
    });
    
    if (error) {
      console.error('❌ Sign in failed:', error.message);
      throw error;
    }
    
    return data;
  },

  async signOut() {
    const client = getSupabaseClient();
    const { error } = await client.auth.signOut();
    
    if (error) {
      console.error('❌ Sign out failed:', error.message);
      throw error;
    }
  },

  async getUser() {
    const client = getSupabaseClient();
    const { data: { user }, error } = await client.auth.getUser();
    
    if (error && error.message !== 'Invalid JWT') {
      console.error('❌ Get user failed:', error.message);
      throw error;
    }
    
    return user;
  },

  async getSession() {
    const client = getSupabaseClient();
    const { data: { session }, error } = await client.auth.getSession();
    
    if (error) {
      console.error('❌ Get session failed:', error.message);
      throw error;
    }
    
    return session;
  },
} as const;

// Default export - simple client getter
const supabase = getSupabaseClient();
export default supabase;